import axios from 'axios';

// Definizione degli URL per diversi ambienti
const ENVIRONMENTS = {
  local: 'http://localhost:4000',
  production: ''  // URL relativo - Apache gestisce tutto senza prefisso /api
};

// Funzione per determinare l'URL da utilizzare
const getBaseUrl = () => {
  // Se siamo in locale, usa l'URL locale
  if (window.location.hostname === 'localhost') {
    return ENVIRONMENTS.local;
  }
  
  // Se siamo sul server di produzione
  if (window.location.hostname === '195.231.61.29') {
    return ENVIRONMENTS.production;
  }
  
  // In altri casi (es. test da altri dispositivi nella rete locale)
  return `http://${window.location.hostname}:4000`;
};

const BASE_URL = getBaseUrl();

// Store per le notifiche globali - sarà popolato dal NotificationManager
let globalNotificationStore = null;

/**
 * Funzione per registrare il sistema di notifiche globale
 * Deve essere chiamata dall'App.js durante l'inizializzazione
 */
export const setGlobalNotificationStore = (notificationStore) => {
  globalNotificationStore = notificationStore;
};

/**
 * Gestione centralizzata degli errori API
 */
const handleApiError = (error) => {
  // Log dell'errore per debugging
  console.error('API Error:', error);

  // Gestioni specifiche per codici di errore
  if (error.response) {
    switch (error.response.status) {
      case 401:
      case 403:
        // Non mostrare notifica e non fare redirect per axiosPrivate - lascia che useAxiosPrivate gestisca il refresh
        if (error.config && error.config._isAxiosPrivate) {
          // Questo è un errore da axiosPrivate, verrà gestito dall'interceptor con refresh
          // Non mostrare popup di errore
          return Promise.reject(error);
        }
        // Per axios pubblico, mostra errore e redirect
        if (globalNotificationStore && globalNotificationStore.showError) {
          globalNotificationStore.showError(error);
        }
        if (error.response.status === 401 && window.location.pathname !== '/login') {
          const BASE_PATH = process.env.REACT_APP_BASE_PATH || '/app-anziani';
          window.location.href = `${BASE_PATH}/login`;
        }
        break;
      default:
        // Per tutti gli altri errori, mostra la notifica
        if (globalNotificationStore && globalNotificationStore.showError) {
          globalNotificationStore.showError(error);
        }
        break;
      case 429:
        // Rate limiting - potresti implementare un sistema di retry
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        // Errori del server - potrebbero necessitare di retry automatico
        break;
    }
  }

  return Promise.reject(error);
};

/**
 * Interceptor per le richieste
 */
const requestInterceptor = (config) => {
  // Aggiungi timestamp per evitare cache
  config.params = {
    ...config.params,
    _t: Date.now()
  };

  // Aggiungi loading indicator se necessario
  if (globalNotificationStore && globalNotificationStore.setLoading) {
    globalNotificationStore.setLoading(true);
  }

  // Log delle richieste in development
  // if (process.env.NODE_ENV === 'development') {
  //   console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
  // }

  return config;
};

/**
 * Interceptor per gli errori delle richieste
 */
const requestErrorInterceptor = (error) => {
  console.error('Request Error:', error);
  
  // Rimuovi loading indicator
  if (globalNotificationStore && globalNotificationStore.setLoading) {
    globalNotificationStore.setLoading(false);
  }

  return Promise.reject(error);
};

/**
 * Interceptor per le risposte
 */
const responseInterceptor = (response) => {
  // Rimuovi loading indicator
  if (globalNotificationStore && globalNotificationStore.setLoading) {
    globalNotificationStore.setLoading(false);
  }

  // Log delle risposte in development
  // if (process.env.NODE_ENV === 'development') {
  //   console.log(`API Response: ${response.status} ${response.config.url}`, response.data);
  // }

  return response;
};

/**
 * Interceptor per gli errori delle risposte
 */
const responseErrorInterceptor = (error) => {
  // Rimuovi loading indicator
  if (globalNotificationStore && globalNotificationStore.setLoading) {
    globalNotificationStore.setLoading(false);
  }

  return handleApiError(error);
};

// Crea istanza axios principale
const axiosInstance = axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
    timeout: 15000 // Timeout di 15 secondi
});

// Configura interceptors per l'istanza principale
axiosInstance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
axiosInstance.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

// Crea istanza axios privata (per richieste autenticate)
export const axiosPrivate = axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
    timeout: 15000
});

// Interceptor specifico per axiosPrivate che marca le richieste
const privateRequestInterceptor = (config) => {
  // Marca questa richiesta come proveniente da axiosPrivate
  config._isAxiosPrivate = true;
  
  // Applica gli altri interceptor standard
  return requestInterceptor(config);
};

// Configura interceptors per l'istanza privata con marcatura
axiosPrivate.interceptors.request.use(privateRequestInterceptor, requestErrorInterceptor);
axiosPrivate.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

// Esporta l'istanza principale come default
export default axiosInstance;